import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { store } from 'components/Store';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import {
  DateFilterButton,
  ChartViewOptions,
  TypeOfChartOptions,
} from 'components/Dashboard/Analytics/ui/AnalyticsFilters/FiltersBar';
import useEngagementStatsState from './useEngagementStatsState';
import useCallEngagementStats from './useCallEngagementStats';
import { COUNT_MODE_OPTIONS, BREAKDOWN_OPTIONS, resolveBreakdownLabel, sortBreakdownByQuantity } from './breakdownDimensions';
import seriesColours from './seriesColours';
import SingleSelectDropdown from './ui/SingleSelectDropdown';
import BreakdownLegend from './ui/BreakdownLegend';
import EngagementStatsChart from './ui/EngagementStatsChart';
import EngagementStatsDataTable from './ui/EngagementStatsDataTable';
import EngagementStatsRightPanel from './ui/EngagementStatsRightPanel';

const CrmEngagementStats = () => {
  const { addMetaTags } = useAddMetaTags();
  useEffect(() => {
    addMetaTags({ title: 'Engagement stats - People - PolicyMogul', hash: null });
  }, []);

  const globalState = useContext(store);
  const { state: globalStoreState, dispatch } = globalState;
  const teamMembers = globalStoreState?.team?.allMembers ?? [];
  const teamName = globalStoreState?.team?.teamName;

  const { state, modifyState } = useEngagementStatsState();
  const { series, breakdown, isLoading } = useCallEngagementStats(state);
  const [activeCell, setActiveCell] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [hiddenBuckets, setHiddenBuckets] = useState([]);
  const [heightContainer, containerRef] = useHeightContainer();
  const chartRef = useRef(null);
  const chartSectionRef = useRef(null);

  // A fresh dimension means a fresh set of bucket keys (types vs teammate ids vs tags) - stale hidden
  // buckets from the previous dimension wouldn't mean anything here.
  useEffect(() => {
    setHiddenBuckets([]);
  }, [state.breakdownBy]);

  // Memoized (rather than a plain arrow function/inline call recreated every render) because a
  // dispatch effect further down depends on values derived from these - see the comment there.
  const labelFor = useCallback(
    (bucket) => resolveBreakdownLabel({ breakdownBy: state.breakdownBy, bucket, teamMembers, teamName }),
    [state.breakdownBy, teamMembers, teamName]
  );

  const sortedBreakdown = useMemo(() => sortBreakdownByQuantity(breakdown), [breakdown]);

  // Computed outside the setState updater, not inside it - an updater is expected to be a pure
  // function of its previous state and React is free to invoke it more than once (it does, in dev
  // StrictMode), so a side effect like createNotification living in there fires twice per click.
  const toggleBucket = (bucket, forceHidden) => {
    const isHidden = hiddenBuckets.includes(bucket);
    const nextHidden = forceHidden ?? !isHidden;
    if (nextHidden === isHidden) return;
    if (nextHidden && sortedBreakdown.length - hiddenBuckets.length <= 1) {
      createNotification('danger', 'You must have at least 1 resource selected');
      return;
    }
    setHiddenBuckets(nextHidden ? [...hiddenBuckets, bucket] : hiddenBuckets.filter((item) => item !== bucket));
  };

  // Memoized - this feeds the dispatch effect below by reference. Recomputing a fresh array every
  // render made that effect's dependency "change" on every render regardless of cause, and since the
  // effect's own dispatch triggers a re-render of every store consumer (this component included via
  // its own useContext(store) above), that was a genuine infinite loop, not just wasted work.
  const chartKeys = useMemo(
    () =>
      sortedBreakdown
        .map((item, index) => ({
          bucket: item.bucket,
          label: labelFor(item.bucket),
          colour: seriesColours[index % seriesColours.length],
        }))
        .filter((item) => !hiddenBuckets.includes(item.bucket)),
    [sortedBreakdown, hiddenBuckets, labelFor]
  );

  // The Download chart / Add to report buttons live in InfluenceCrmSubNav (a different branch of the
  // component tree), so what they need is published here rather than rendered inline - see
  // EngagementStatsSubNavActions.jsx.
  useEffect(() => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'engagementStatsChartState',
      value: { chartSectionRef, localState: state, chartKeys },
    });
    return () => {
      dispatch({ type: 'MODIFY_SECTION', parameter: 'engagementStatsChartState', value: {} });
    };
  }, [state, chartKeys]);

  return (
    <div className='main-content pr-xl-5 px-md-4 px-3 mt-2'>
      <div ref={containerRef} />
      <div className='d-flex' style={{ height: `${heightContainer}px` }}>
        <div className='flex-grow-1 bg-main-white border rounded-top' style={{ minWidth: 0 }}>
          <CustomScrollbar className='simple-scrollbar smooth-scrolling' style={{ height: '100%' }}>
            <div className='px-lg-5 px-3'>
              <div className='my-5'>
                <CustomScrollbar className='chart-buttons-scrollbar'>
                  <div className='d-flex align-items-center'>
                    <SingleSelectDropdown
                      options={COUNT_MODE_OPTIONS}
                      selectedValue={state.countMode}
                      onSelect={(value) => modifyState({ property: 'countMode', newValue: value })}
                      primary
                    />
                    <div className='ml-3'>
                      <SingleSelectDropdown
                        options={BREAKDOWN_OPTIONS}
                        selectedValue={state.breakdownBy}
                        onSelect={(value) => modifyState({ property: 'breakdownBy', newValue: value })}
                      />
                    </div>
                    <div className='ml-3'>
                      <DateFilterButton analyticsState={state} modifyFiltersBar={modifyState} customLeftValue={0} />
                    </div>
                    <TypeOfChartOptions analyticsState={state} modifyFiltersBar={modifyState} />
                    <ChartViewOptions analyticsState={state} modifyFiltersBar={modifyState} />
                  </div>
                </CustomScrollbar>
              </div>
              <BreakdownLegend
                breakdown={sortedBreakdown}
                labelFor={labelFor}
                hiddenBuckets={hiddenBuckets}
                onToggle={toggleBucket}
              />
              <div className='mt-4' ref={chartSectionRef}>
                <EngagementStatsChart
                  ref={chartRef}
                  breakdown={sortedBreakdown}
                  hiddenBuckets={hiddenBuckets}
                  labelFor={labelFor}
                  typeOfChart={state.typeOfChart}
                  currentView={state.currentView}
                  countMode={state.countMode}
                  isLoading={isLoading}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                />
              </div>
              <EngagementStatsDataTable
                totalSeries={series?.[0]}
                breakdown={sortedBreakdown}
                labelFor={labelFor}
                currentView={state.currentView}
                activeCell={activeCell}
                onCellClick={(cell) => setActiveCell(cell)}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
              />
            </div>
          </CustomScrollbar>
        </div>
        {activeCell && (
          <EngagementStatsRightPanel
            cell={activeCell}
            breakdownBy={state.breakdownBy}
            currentView={state.currentView}
            onClose={() => setActiveCell(null)}
          />
        )}
      </div>
    </div>
  );
};

export default CrmEngagementStats;
